// PROD-1227: one owner for the General-path /ai/augment-category calls.
//
// WHY. Each of the four categories (details, signs_symptoms, context, additional_factors) gets
// its AI follow-ups from one Sonnet call (6.3 s p50, 11.3 s max in the TEST load run). The call
// for a category reads that category's SAVED answers, so it cannot start before the patient has
// answered it; the follow-ups render on the NEXT screen. Before this module every screen that
// needed a result started its own call:
//   - handleNext started one 800 ms after leaving the category (a blind wait for the save);
//   - the next screen's mount effect started a SECOND one at once, without looking at the first,
//     usually before the save had landed, so it could be built without the answers just given;
//   - the adaptive screen (additional_factors) polled sessionStorage once a second for the
//     handleNext call, so the patient waited 0.8 s + the call + up to 1 s on a full-screen loader.
// Two calls per category also meant the screen showed one set while sessionStorage (the dedup
// list sent as `priorQuestions` for later categories, and the saved question text) held the other.
//
// WHAT THIS DOES. `startAugment` is single-flight per (case, category): the first caller starts
// the call, every later caller (the next screen, the adaptive screen) joins the same promise. A
// call started from handleNext fires as soon as the answers are saved, never later than 800 ms
// (the old fixed delay). `priorQuestions` is read when the call fires, as before. When a result
// arrives, a follow-up whose normalised text matches a question an EARLIER category already
// produced is dropped: calls for neighbouring categories overlap when the patient is quicker
// than the AI, and the server can only dedup against what the earlier call had stored by then.
// The promise never rejects: a failed or empty call resolves null and every caller keeps its
// existing fallback (no "A few more" section; the adaptive screen skips to the summary).
//
// No PHI is logged: category names and counts only, and on a failure the error's status and name
// (never its message: apiFetch throws the server's error JSON). The module calls `console` directly,
// never through an injected logger, so scripts/check-console-phi.mjs scans every call here.

export const AUGMENT_CATEGORIES = ['details', 'signs_symptoms', 'context', 'additional_factors']

// The longest a handleNext call waits for the answers to be saved before it fires anyway.
export const SAVE_WAIT_CAP_MS = 800

const resultKey = (caseId, cat) => `orella_augment_${caseId}_${cat}`
const inflightKey = (caseId, cat) => `orella_augment_inflight_${caseId}_${cat}`

const inFlight = new Map()
const flightKey = (caseId, cat) => `${caseId}__${cat}`

const defaultStorage = () => {
  try { return globalThis.sessionStorage || null } catch (_) { return null }
}

function readQuestions(storage, caseId, cat) {
  try {
    const raw = storage?.getItem(resultKey(caseId, cat))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed?.questions) ? parsed.questions : []
  } catch (_) { return [] }
}

// Every augment question TEXT the patient has been shown for this case, from the sessionStorage
// cache, except `excludeCategory`'s own (v226.10.0; sent to the server as `priorQuestions`).
export function collectSeenAugmentQuestions(caseId, excludeCategory = null, storage = defaultStorage()) {
  if (!caseId) return []
  const texts = []
  for (const cat of AUGMENT_CATEGORIES) {
    if (cat === excludeCategory) continue
    for (const q of readQuestions(storage, caseId, cat)) {
      if (q?.text) texts.push(q.text)
    }
  }
  return texts
}

// Case, punctuation and spacing do not make a question new.
export function normaliseQuestionText(text) {
  return String(text || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\p{P}\p{S}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Drop the questions of `category` whose text an EARLIER category already produced.
export function dropEarlierDuplicates(caseId, category, questions, storage = defaultStorage()) {
  const idx = AUGMENT_CATEGORIES.indexOf(category)
  const earlier = new Set()
  for (const cat of AUGMENT_CATEGORIES.slice(0, Math.max(0, idx))) {
    for (const q of readQuestions(storage, caseId, cat)) {
      const n = normaliseQuestionText(q?.text)
      if (n) earlier.add(n)
    }
  }
  if (!earlier.size) return questions
  return questions.filter((q) => !earlier.has(normaliseQuestionText(q?.text)))
}

// The call already running for (case, category), or null.
export function augmentInFlight(caseId, category) {
  return inFlight.get(flightKey(caseId, category)) || null
}

const waitForSave = (afterSaved, capMs) => {
  if (!afterSaved || typeof afterSaved.then !== 'function') return null
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, Math.max(0, capMs))
    Promise.resolve(afterSaved).catch(() => {}).finally(() => { clearTimeout(timer); resolve() })
  })
}

/**
 * Start (or join) the augment call for one category. Resolves the result ({ questions, ... })
 * or null; never rejects.
 *   fetchAugment(caseId, category, language, priorQuestions) — aiAPI.augmentCategory
 *   afterSaved — the save of this category's answers (handleNext); the call fires when it
 *     settles or after `saveWaitCapMs`, whichever comes first. Without it the call fires now.
 */
export function startAugment({ caseId, category, language = 'en', fetchAugment, afterSaved = null, saveWaitCapMs = SAVE_WAIT_CAP_MS, storage = defaultStorage() }) {
  if (!caseId || !AUGMENT_CATEGORIES.includes(category) || typeof fetchAugment !== 'function') return Promise.resolve(null)
  const key = flightKey(caseId, category)
  const running = inFlight.get(key)
  if (running) return running

  const cached = readQuestions(storage, caseId, category)
  if (cached.length > 0) {
    try { return Promise.resolve(JSON.parse(storage.getItem(resultKey(caseId, category)))) } catch (_) { /* fall through to a fresh call */ }
  }

  try { storage?.setItem(inflightKey(caseId, category), 'true') } catch (_) { /* sessionStorage unavailable */ }

  const fire = () => Promise.resolve()
    .then(() => fetchAugment(caseId, category, language, collectSeenAugmentQuestions(caseId, category, storage)))

  const gate = waitForSave(afterSaved, saveWaitCapMs)
  const call = (gate ? gate.then(fire) : fire())
    .then((result) => {
      const got = Array.isArray(result?.questions) ? result.questions : []
      const kept = dropEarlierDuplicates(caseId, category, got, storage)
      if (kept.length < got.length) console.log(`[augment] ${category}: dropped ${got.length - kept.length} follow-up(s) an earlier category already asked`)
      if (kept.length === 0) {
        console.log(`[augment] ${category}: no follow-ups`)
        return null
      }
      const out = kept.length === got.length ? result : { ...result, questions: kept, questionCount: kept.length }
      try { storage?.setItem(resultKey(caseId, category), JSON.stringify(out)) } catch (_) { /* quota — non-fatal */ }
      console.log(`[augment] ${category}: ${kept.length} follow-up(s)`)
      return out
    })
    .catch((err) => {
      console.warn(`[augment] ${category}: call failed (non-fatal):`, err?.status, err?.name)
      return null
    })
    .finally(() => {
      inFlight.delete(key)
      try { storage?.removeItem(inflightKey(caseId, category)) } catch (_) { /* sessionStorage unavailable */ }
    })
  inFlight.set(key, call)
  return call
}
